import { useEffect } from 'react';
// @ts-expect-error
import { provideHooks } from 'redial';
import loadable from 'sku/@loadable/component';

import PoeaCountryPicker from 'src/components/Poea/PoeaCountryPicker';
import { SafetyHelmet } from 'src/components/SafetyHelmet/SafetyHelmet';
import { BehavioralCuesFiltersProvider } from 'src/components/SearchResultPage/BehaviouralCuesFiltersContext/BehaviouralCuesFiltersContext';
import {
  SEARCH_QUERY_CORRECTION_FEATURE_FLAG,
  type SearchQueryCorrectionExperiment,
} from 'src/config/experiments';
import { BannerPlacement } from 'src/graphql/graphql';
import { useIntercom } from 'src/hooks/useIntercom';
import { useSaveJobFromQueryParam } from 'src/hooks/useSaveJob/utils/useSaveJobFromQueryParam';
import {
  isBrowserAnalyticsFacade,
  type JobBadgesFilter,
} from 'src/modules/AnalyticsFacade';
import { useSearchResultsHead } from 'src/modules/seo-header';
import { getBanner } from 'src/store/banner';
import { clearJobDetails } from 'src/store/jobdetails';
import { useDispatch, useSelector } from 'src/store/react';
import { getRecentSearches } from 'src/store/recentSearches';
import { getResults, saveSolReferencesAction } from 'src/store/results';
import { expandSearchFilters, persistCriteriaLocally } from 'src/store/search';
import {
  selectLocation,
  selectPoeaCountryPicker,
  selectSearchPerformedAnalyticsData,
} from 'src/store/selectors';
import type { RedialLocals } from 'src/types/RedialLocals';
import { getFeatureFlagWithOverridesHelper } from 'src/utils/getFeatureFlagWithOverrides';

import { SplitViewContextProvider, useSplitView } from './SplitViewContext';

export const SearchResultPage = loadable(
  /* #__LOADABLE__ */ () =>
    import(
      /* webpackChunkName: "SearchResultPage" */ 'src/components/SearchResultPage/SearchResultPage'
    ),
  {
    resolveComponent: (m) => m.default,
  },
);

const hooks = {
  // this is the initial fetch for the search results page in server-side rendering
  fetch: ({
    analyticsFacade,
    apolloClient,
    brand,
    cookies,
    country,
    dispatch,
    getState,
    locale,
    path,
    query,
    serverProps = {},
    userAgent,
    visitorId,
    zone,
  }: RedialLocals) => {
    const getFeatureFlag = getFeatureFlagWithOverridesHelper({
      apolloClient,
      getState,
    });

    const isSQCEnabled =
      getFeatureFlag<SearchQueryCorrectionExperiment>(
        SEARCH_QUERY_CORRECTION_FEATURE_FLAG,
      ) === 'search-query-correction';

    const searchArgs = {
      apolloClient,
      brand,
      cookies,
      country,
      locale,
      path,
      query,
      serverProps,
      userAgent,
      visitorId,
      zone,
      isSQCEnabled,
    };
    dispatch(expandSearchFilters(true));

    // Get the latest recent searches
    dispatch(getRecentSearches({ cookies, country }));

    return dispatch(getResults(searchArgs)).then(() => {
      const currentState = getState();
      const isPoeaCountryPicker = selectPoeaCountryPicker(currentState);
      if (!isPoeaCountryPicker) {
        const payload = selectSearchPerformedAnalyticsData(currentState);
        analyticsFacade.searchPerformed(payload);
      }
    });
  },
  defer: ({ dispatch, routeEnter }: RedialLocals) => {
    if (routeEnter && window.hj) {
      window.hj('trigger', 'search-results-all-browsers');
    }
    return dispatch(persistCriteriaLocally());
  },
  pageLoad: ({
    analyticsFacade,
    apolloClient,
    dispatch,
    getState,
    query,
  }: RedialLocals) => {
    const {
      appConfig: {
        zoneFeatures: { LMIS_ENABLED },
      },
      results: { pills, solMetadata, isLoading },
      search: { poeaCountryPicker, dynamicSearchBarConfiguration },
      user: { userClientId },
    } = getState();

    if (LMIS_ENABLED) {
      dispatch(
        getBanner({
          apolloClient,
          placement: BannerPlacement.SerpFooterLinks,
          visitorId: userClientId,
        }),
      );
    }

    if (
      !isLoading &&
      isBrowserAnalyticsFacade(analyticsFacade) &&
      !poeaCountryPicker
    ) {
      const searchResultsTab = query?.tags?.includes('new')
        ? 'New to you'
        : 'All';

      analyticsFacade.searchResultsPageLoaded(
        {
          refinedKeywords: pills?.map(({ isActive, label }, index) => ({
            isActive,
            label,
            position: index,
          })),
          solMetadata,
          searchResultsTab,
          jobBadgesFilter: query?.tags
            ? ([query?.tags] as JobBadgesFilter)
            : [],
          searchBarConfiguration: dynamicSearchBarConfiguration,
        },
        {
          flightId: solMetadata?.tags?.mordor__flights ?? '',
          linkPosition: query?.pos,
          jobBadgesFilter: query?.tags ? [query?.tags] : [],
          searchResultsTab,
        },
      );
    }

    dispatch(saveSolReferencesAction());
  },
};

function useClearJobDetailsOnLocationChange() {
  const dispatch = useDispatch();
  const currentUrl = useSelector(selectLocation).url;

  // clear job details for splitview
  useEffect(() => {
    const action = clearJobDetails();
    dispatch(action);
  }, [currentUrl, dispatch]);
}

const SearchResults = () => {
  useEffect(() => {
    window.hj =
      window.hj ||
      function () {
        // eslint-disable-next-line prefer-rest-params
        (hj.q = hj.q || []).push(arguments);
      };
  });

  useIntercom();

  useClearJobDetailsOnLocationChange();

  useSaveJobFromQueryParam();

  const helmetData = useSearchResultsHead();

  const splitViewContextValue = useSplitView();

  const isCountryPicker = useSelector(selectPoeaCountryPicker);

  return (
    <>
      <SafetyHelmet {...helmetData} key="head" />
      <SplitViewContextProvider value={splitViewContextValue}>
        {isCountryPicker ? (
          <PoeaCountryPicker />
        ) : (
          <BehavioralCuesFiltersProvider>
            <SearchResultPage key="body" />
          </BehavioralCuesFiltersProvider>
        )}
      </SplitViewContextProvider>
    </>
  );
};

export default provideHooks(hooks)(SearchResults);
